import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { validate } from 'validate.js';

import { cloneDeep } from 'es-toolkit';
import { Alert, Box, Form, Heading, List, Modal, Paragraph, Text, Toast, useToast } from '@zillow/constellation-10';
import {
  DatarouterError,
  FeeCalculationType,
  FeeTypeGrouping,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_create,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_update,
} from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { useSelector } from '../../../selectors/useSelector';

import { removeErrorKey } from '../../../utils/formValidation';
import { costsAndFeesConstraints } from '../../../validation';

import ModalFooter from '../ModalFooter';

import FeeAmountField from '../../CostsAndFeesFields/FeeAmountField';
import FeeDropdownSelectField from '../../CostsAndFeesFields/FeeDropdownSelectField';
import FeeRadioGroupField from '../../CostsAndFeesFields/FeeRadioGroupField';

import type {
  CreateSingleFamilyFeesRequestDto,
  FeeCalculationDictionaryItemDisplayDto,
  FeeCategoryDictionaryItemDto,
  FeeDictionaryItemDisplayDto,
  FeeRefundableType,
  FeeRequirementType,
  FeeTimingSubType,
  FeeType,
  GetSingleFamilyFeesResponseDto,
  SingleFamilyFeeDto,
  UpdateSingleFamilyFeesRequestDto,
} from '@zg-rentals/rental-manager-env';

type Props = {
  onClose: () => void;
  feeToEdit?: SingleFamilyFeeDto | null;
  fixedCategory: string;
};

const SingleFamilyCostsAndFeesModal = ({ onClose, feeToEdit = null, fixedCategory = '' }: Props) => {
  const queryClient = useQueryClient();

  const topRef = useRef<HTMLDivElement>(null);

  const listingAlias = useSelector((state) => state.properties.currentProperty.listingAlias);

  const [feeAmountMax, setFeeAmountMax] = useState(
    feeToEdit?.feeAmountMax && feeToEdit?.feeCalculationType === 'RANGE' ? feeToEdit.feeAmountMax : (0 as number),
  );
  const [feeAmountMin, setFeeAmountMin] = useState(feeToEdit?.feeAmountMin || (0 as number));
  const [feeCategory, setFeeCategory] = useState(fixedCategory || '');
  const [feeType, setFeeType] = useState(feeToEdit?.feeType || '');
  const [feePaymentFrequencyType, setFeePaymentFrequencyType] = useState(feeToEdit?.feePaymentFrequencyType || '');
  const [feeRefundableType, setFeeRefundableType] = useState(feeToEdit?.feeRefundableType || '');
  const [feeRequirementType, setFeeRequirementType] = useState(feeToEdit?.feeRequirementType || '');
  const [feeFormat, setFeeFormat] = useState(feeToEdit?.feeCalculationType || '');

  const [feeErrors, setFeeErrors] = useState<Record<string, string>>({});

  const [feeCreateOrUpdateError, setFeeCreateOrUpdateError] = useState<React.ReactNode | null>(null);

  const { enqueueToast } = useToast();

  const { data } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get({
    listingAlias,
  });

  const mainCategoriesKeys = Object.keys(data!.dictionaryItems.mainCategories);

  const { mutate: createFee, isPending: isCreatingFee } =
    useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_create({
      onSuccess: (response: SingleFamilyFeeDto) => {
        // Define the query key
        const queryKey = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get.getKey({
          listingAlias,
        });

        // Add the new fee to the cache
        queryClient.setQueryData<GetSingleFamilyFeesResponseDto>(
          queryKey,
          (prevFeesData?: GetSingleFamilyFeesResponseDto) => {
            if (!prevFeesData) {
              return {} as GetSingleFamilyFeesResponseDto;
            }

            const feesData = cloneDeep(prevFeesData);

            if (feesData?.existingFees) {
              // check if the response.feeCategory is a main category
              if (Object.keys(feesData.dictionaryItems.mainCategories).includes(response.feeCategory)) {
                const category = response.feeCategory as keyof typeof feesData.existingFees.mainCategories;
                // check if the fee category already exists in mainCategories
                const existingFeeCategory = feesData!.existingFees.mainCategories[category];
                if (existingFeeCategory) {
                  // append the new fee at the end of that category's fee list
                  feesData!.existingFees.mainCategories[category].push(response);
                } else {
                  // insert a new key for that category and include the new fee under it
                  feesData.existingFees.mainCategories[category] = [response];
                }
              } else {
                // response.feeCategory is not a main category
                // push the new fee to the "other" category
                feesData.existingFees.otherCategories.push(response);
              }
            }

            return feesData as GetSingleFamilyFeesResponseDto;
          },
        );

        onClose();

        enqueueToast(<Toast tone="success">Your new fee was added successfully.</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to create fee for listingAlias: ${listingAlias}`,
            details: 'Create single family fee',
          },
        });

        const { errors } = DatarouterError.getErrorDetails(err);
        if (errors) {
          const formattedErrors = formatCreateOrUpdateErrors(
            "Your new fee couldn't be added. Please try again. Error details:",
            errors.map((error) => ({ message: error.message })),
          );
          setFeeCreateOrUpdateError(formattedErrors);
        }

        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    });

  const { mutate: updateFee, isPending: isUpdatingFee } =
    useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_update({
      onSuccess: (response: SingleFamilyFeeDto) => {
        // set query data to include the updated fee
        // Define the query key
        const queryKey = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get.getKey({
          listingAlias,
        });

        // Update the fee in the cache: find that fee in the existingFees object by its feeCategory and costAndFeeId. replace that item with the updated fee object.
        queryClient.setQueryData<GetSingleFamilyFeesResponseDto>(
          queryKey,
          (prevFeesData?: GetSingleFamilyFeesResponseDto) => {
            if (!prevFeesData) {
              return {} as GetSingleFamilyFeesResponseDto;
            }

            const feesData = cloneDeep(prevFeesData);

            if (Object.keys(feesData.dictionaryItems.mainCategories).includes(response.feeCategory)) {
              const category = response.feeCategory as keyof typeof feesData.existingFees.mainCategories;
              // find the existing fee that has the costAndFeeId. and then replace it
              const existingFeeCategory = feesData!.existingFees.mainCategories[category];
              const existingFeeIndex = existingFeeCategory.findIndex(
                (existingFee) => existingFee.costAndFeeId === response.costAndFeeId,
              );
              if (existingFeeIndex > -1) {
                existingFeeCategory[existingFeeIndex] = response;
              }
            } else {
              // response.feeCategory is not a main category
              // find the existing fee that has the costAndFeeId. and then replace it
              const existingFeeIndex = feesData.existingFees.otherCategories.findIndex(
                (existingFee) => existingFee.costAndFeeId === response.costAndFeeId,
              );
              if (existingFeeIndex > -1) {
                feesData.existingFees.otherCategories[existingFeeIndex] = response;
              }
            }

            return feesData as GetSingleFamilyFeesResponseDto;
          },
        );

        onClose();

        enqueueToast(<Toast tone="success">Your changes were saved successfully.</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to update fee for listingAlias: ${listingAlias}, feeGroupId: ${feeToEdit?.costAndFeeId}`,
            details: 'Update single family fee',
          },
        });

        const formattedErrors = formatCreateOrUpdateErrors(
          "Your changes couldn't be saved. Please try again. Error details:",
          DatarouterError.getErrorDetails(err).errors,
        );
        setFeeCreateOrUpdateError(formattedErrors);

        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    });

  const validateField = (fieldName: string, fieldValue: string | number) => {
    let error = '';
    let feeErrorObj = feeErrors;
    const maxGreaterThanMinErrorMessage = 'Maximum amount must be greater than minimum amount';

    // if feeFormat was changed, clear out the feeAmount errors since those values got cleared out
    if (fieldName === 'feeFormat') {
      feeErrorObj = {
        ...feeErrorObj,
        feeAmountMin: '',
        feeAmountMax: '',
        feeAmountMinFlat: '',
        feeAmountPercent: '',
        feeAmountMultiple: '',
      };
    }
    if (feeFormat === FeeCalculationType.RANGE && (fieldName === 'feeAmountMin' || fieldName === 'feeAmountMax')) {
      // if editing amounts for feeFormat RANGE, clear out the error maxGreaterThanMinErrorMessage. will revalidate max>min for the current field below
      // keeping this separate so that if the fieldValue for amount fields have another error, it can get validated separately below
      feeErrorObj = {
        ...feeErrorObj,
        feeAmountMin: feeErrors.feeAmountMin === maxGreaterThanMinErrorMessage ? '' : feeErrors.feeAmountMin,
        feeAmountMax: feeErrors.feeAmountMax === maxGreaterThanMinErrorMessage ? '' : feeErrors.feeAmountMax,
      };
      if (
        (fieldName === 'feeAmountMin' && feeAmountMax <= (fieldValue as number)) ||
        (fieldName === 'feeAmountMax' && feeAmountMin >= (fieldValue as number))
      ) {
        error = maxGreaterThanMinErrorMessage;
      } else {
        let errorMin = '';
        let errorMax = '';
        // need to validate min and max every time one of the two range fields changes
        const constraintsForMin = costsAndFeesConstraints.values?.['feeAmountMin'];
        if (constraintsForMin) {
          const validationErrorMin = validate(
            { ['feeAmountMin']: feeAmountMin },
            { ['feeAmountMin']: constraintsForMin },
          )?.['feeAmountMin']?.[0];
          if (validationErrorMin) {
            errorMin = removeErrorKey('feeAmountMin', validationErrorMin);
          }
        }

        const constraintsForMax = costsAndFeesConstraints.values?.['feeAmountMax'];
        if (constraintsForMax) {
          const validationErrorMax = validate(
            { ['feeAmountMax']: feeAmountMax },
            { ['feeAmountMax']: constraintsForMax },
          )?.['feeAmountMax']?.[0];
          if (validationErrorMax) {
            errorMax = removeErrorKey('feeAmountMax', validationErrorMax);
          }
        }

        return setFeeErrors({
          ...feeErrorObj,
          feeAmountMin: errorMin,
          feeAmountMax: errorMax,
        });
      }
    } else {
      const constraintsForField = costsAndFeesConstraints.values?.[fieldName];
      if (constraintsForField) {
        const validationError = validate({ [fieldName]: fieldValue }, { [fieldName]: constraintsForField })?.[
          fieldName
        ]?.[0];
        if (validationError) {
          error = removeErrorKey(fieldName, validationError);
        }
      }
    }
    setFeeErrors({
      ...feeErrorObj,
      [fieldName]: error,
    });
  };

  const handleFeeCategoryChange = (value: string) => {
    setFeeCategory(value);
    setFeeType('');
  };

  const handleFeeFormatChange = (value: string) => {
    setFeeFormat(value);
    setFeeAmountMax(0);

    // Clear feeAmountMin for VARIABLE_USAGE_BASED since it has no input field
    if (value === FeeCalculationType.VARIABLE_USAGE_BASED) {
      setFeeAmountMin(0);
    }

    validateField('feeFormat', value);
  };

  const canSubmit = useMemo<boolean>(() => {
    switch (true) {
      case feeFormat === FeeCalculationType.RANGE && (!feeAmountMax || !feeAmountMin):
        return false;
      case feeFormat !== FeeCalculationType.FLAT_FEE &&
        feeFormat !== FeeCalculationType.VARIABLE_USAGE_BASED &&
        feeAmountMin === 0:
        return false;
      case isNaN(feeAmountMin) ||
        isNaN(feeAmountMax) ||
        !feeFormat ||
        !feeCategory ||
        !feeType ||
        !feePaymentFrequencyType ||
        !feeRequirementType:
        return false;
      case Object.values(feeErrors).some((error) => error !== ''):
        return false;
      default:
        return true;
    }
  }, [
    feeAmountMin,
    feeAmountMax,
    feeFormat,
    feeCategory,
    feeType,
    feePaymentFrequencyType,
    feeRequirementType,
    feeErrors,
  ]);

  const onSubmit = () => {
    const postData = {
      feeCalculationType: feeFormat as FeeCalculationType,
      feeCategory: feeCategory as FeeTypeGrouping,
      feeType: feeType as FeeType,
      feePaymentFrequencyType: feePaymentFrequencyType as FeeTimingSubType,
      feeRequirementType: feeRequirementType as FeeRequirementType,
      listingAlias,
    } as CreateSingleFamilyFeesRequestDto;

    // Only include feeAmountMin and feeAmountMax for fee types that have amount fields
    if (feeFormat !== FeeCalculationType.VARIABLE_USAGE_BASED) {
      postData.feeAmountMin = feeAmountMin;
      postData.feeAmountMax = feeAmountMax || feeAmountMin;
    }

    if (feeRefundableType !== '') {
      postData.feeRefundableType = feeRefundableType as FeeRefundableType;
    }

    if (feeToEdit) {
      // cast postData from the CreateSingleFamilyFeesRequestDto type to UpdateSingleFamilyFeesRequestDto
      const updateData: UpdateSingleFamilyFeesRequestDto = {
        ...postData,
        costAndFeeId: feeToEdit.costAndFeeId,
      };
      updateFee({ postData: updateData });
    } else {
      createFee({ postData });
    }
  };

  const formatCreateOrUpdateErrors = (generalMessage: string, errors: Array<{ message: string }>) => {
    return (
      <>
        <Paragraph>{generalMessage}</Paragraph>
        <List.Root>
          {errors.map((error) => (
            <List.Item key={error.message}>{error.message}</List.Item>
          ))}
        </List.Root>
      </>
    );
  };

  let modalHeading;
  switch (true) {
    case !!feeToEdit:
      modalHeading = 'Edit a fee';
      break;
    case fixedCategory === FeeTypeGrouping.ADMIN:
      modalHeading = `Add an administrative fee`;
      break;
    case fixedCategory === FeeTypeGrouping.UTILITIES:
      modalHeading = `Add a utilities fee`;
      break;
    case fixedCategory === FeeTypeGrouping.PARKING:
      modalHeading = `Add a parking fee`;
      break;
    default:
      modalHeading = 'Add a fee';
  }

  return (
    <Modal
      open={true}
      onOpenChange={() => onClose()}
      header={<Heading level={5}>{modalHeading}</Heading>}
      body={
        <Box css={{ paddingBottom: '400' }}>
          <Box css={{ marginBottom: '600' }}>
            <Text asChild>
              <p>
                Let renters know the fee type and amount, when it&apos;s charged, and whether it&apos;s included in the
                base rent. These details will appear on your listing.{' '}
              </p>
            </Text>
          </Box>

          {feeCreateOrUpdateError && (
            <Box css={{ paddingBottom: '400' }}>
              <Alert tone="critical">{feeCreateOrUpdateError}</Alert>
            </Box>
          )}

          <Form asChild={true} onSubmit={(e) => e.preventDefault()}>
            <div>
              {/* Category field */}
              {/* do not show the category field if the category is already selected prior to opening the modal */}
              {fixedCategory !== FeeTypeGrouping.ADMIN &&
                fixedCategory !== FeeTypeGrouping.UTILITIES &&
                fixedCategory !== FeeTypeGrouping.PARKING && (
                  <FeeDropdownSelectField
                    value={feeCategory}
                    onChange={handleFeeCategoryChange}
                    dictionaryItems={
                      mainCategoriesKeys.includes(feeCategory as string)
                        ? (data?.dictionaryItems?.mainCategories as Record<string, FeeCategoryDictionaryItemDto>)
                        : (data?.dictionaryItems?.otherCategories.categories as Record<
                            string,
                            FeeCategoryDictionaryItemDto
                          >)
                    }
                    fieldLabel="Category"
                    required={true}
                    onBlur={validateField}
                    errorMessage={feeErrors.feeCategory ?? ''}
                    fieldName="feeCategory"
                    hasIcon={true}
                    formHelpText={fixedCategory === '' ? 'Select the category your fee should go in.' : ''}
                    readOnly={feeToEdit !== null}
                  />
                )}

              <FeeDropdownSelectField
                required={true}
                value={feeType}
                fieldLabel="Fee name"
                disabled={feeCategory === ''}
                onChange={setFeeType}
                dictionaryItems={
                  mainCategoriesKeys.includes(feeCategory as string)
                    ? (data?.dictionaryItems?.mainCategories[feeCategory as FeeTypeGrouping]?.feeType as Record<
                        string,
                        FeeDictionaryItemDisplayDto
                      >)
                    : (data?.dictionaryItems?.otherCategories.categories[feeCategory as FeeTypeGrouping]
                        ?.feeType as Record<string, FeeDictionaryItemDisplayDto>)
                }
                onBlur={validateField}
                fieldName="feeName"
                errorMessage={feeErrors.feeName ?? ''}
                formHelpText="Select a fee name."
              />

              {/* Payment frequency */}
              <FeeDropdownSelectField
                required={true}
                value={feePaymentFrequencyType}
                onChange={setFeePaymentFrequencyType}
                dictionaryItems={
                  data?.dictionaryItems?.feePaymentFrequencyType as Record<string, FeeDictionaryItemDisplayDto>
                }
                fieldLabel="Payment frequency"
                onBlur={validateField}
                fieldName="feePaymentFrequencyType"
                formHelpText="Select how often this fee should be paid."
                errorMessage={feeErrors.feePaymentFrequencyType ?? ''}
              />

              {/* Required */}
              <FeeRadioGroupField
                value={feeRequirementType}
                onChange={setFeeRequirementType}
                enums={data?.dictionaryItems?.feeRequirementType as Record<string, FeeDictionaryItemDisplayDto>}
                label="Is this fee required?"
                required={true}
              />

              {/* Refundable */}
              <FeeRadioGroupField
                orientation="horizontal"
                value={feeRefundableType}
                onChange={setFeeRefundableType}
                enums={data?.dictionaryItems?.feeRefundableType as Record<string, FeeDictionaryItemDisplayDto>}
                label="Is this fee refundable?"
                required={false}
              />

              {/* Fee format */}
              <FeeDropdownSelectField
                value={feeFormat}
                onChange={handleFeeFormatChange}
                dictionaryItems={
                  data?.dictionaryItems?.feeCalculationType as Record<string, FeeDictionaryItemDisplayDto>
                }
                fieldLabel="Fee format"
                required={true}
                onBlur={validateField}
                fieldName="feeFormat"
                errorMessage={feeErrors.feeFormat ?? ''}
                formHelpText="Select how the fee amount should be shown. Ex: A flat fee of $50, or a range of $25-$45."
              />

              {/*  Fee amount - 1 or 2 inputs depending if fee format is a range */}
              <FeeAmountField
                setFeeAmountMin={setFeeAmountMin}
                setFeeAmountMax={setFeeAmountMax}
                valueMin={feeAmountMin}
                valueMax={feeAmountMax}
                feeFormat={
                  (data?.dictionaryItems?.feeCalculationType[
                    feeFormat as FeeCalculationType
                  ] as FeeCalculationDictionaryItemDisplayDto) ??
                  ('FIXED' as unknown as FeeCalculationDictionaryItemDisplayDto)
                }
                feeFormatName={feeFormat as FeeCalculationType}
                onBlur={validateField}
                feeErrors={feeErrors}
              />
            </div>
          </Form>
        </Box>
      }
      footer={
        <ModalFooter
          submitText="Save"
          onSubmit={onSubmit}
          onCancel={() => onClose()}
          isSubmitting={isCreatingFee || isUpdatingFee}
          isSubmitDisabled={!canSubmit}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{
        escapeKey: true,
        outsidePress: false,
      }}
      data-testid="SingleFamilyCostsAndFeesModal"
    />
  );
};

export default SingleFamilyCostsAndFeesModal;
